import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentMemoryEntry } from '@magimp/shared';
import {
  approveAgentMemoryEntry,
  archiveAgentMemoryEntry,
  createAgentMemoryEntry,
  listAgentMemoryEntries,
  patchAgentMemoryEntry,
  rejectAgentMemoryEntry,
} from '../../api/agent/index.js';
import { Button, Input, Textarea } from '../../ui/index.js';

export function AgentMemoryPanel() {
  const { t } = useTranslation('agent');
  const [items, setItems] = useState<AgentMemoryEntry[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await listAgentMemoryEntries();
      setItems(result.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.loadFailed'));
    } finally {
      setBusy(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setTitle('');
    setSummary('');
  };

  const edit = (item: AgentMemoryEntry) => {
    setEditingId(item.id);
    setTitle(item.title);
    setSummary(item.summary);
  };

  const save = async () => {
    const nextTitle = title.trim();
    const nextSummary = summary.trim();
    if (nextTitle === '' || nextSummary === '') return;
    setBusy(true);
    try {
      if (editingId === null)
        await createAgentMemoryEntry({ title: nextTitle, summary: nextSummary });
      else await patchAgentMemoryEntry(editingId, { title: nextTitle, summary: nextSummary });
      resetForm();
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        <h3 style={{ fontSize: 14, margin: 0, flex: '1 1 auto' }}>{t('admin.memory')}</h3>
        <Button kind="ghost" icon="rotate-ccw" onClick={load} disabled={busy}>
          {t('admin.refresh')}
        </Button>
      </header>
      {error && <p style={{ color: 'var(--status-error-fg)', margin: 0 }}>{error}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 'var(--s-2)' }}>
        <Input value={title} onChange={setTitle} placeholder={t('admin.memoryTitlePlaceholder')} />
        <Textarea
          value={summary}
          rows={2}
          onChange={setSummary}
          ariaLabel={t('admin.memorySummary')}
          placeholder={t('admin.memorySummaryPlaceholder')}
        />
        <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <Button kind="secondary" icon="save" onClick={save} disabled={busy}>
            {editingId === null ? t('admin.addMemory') : t('admin.saveMemory')}
          </Button>
          {editingId !== null && (
            <Button kind="ghost" icon="x" onClick={resetForm} disabled={busy}>
              {t('admin.cancel')}
            </Button>
          )}
        </div>
      </div>
      <AdminList
        items={items}
        render={(item) => (
          <Row key={item.id}>
            <strong>{item.title}</strong>
            <span>{item.status}</span>
            <Button kind="ghost" icon="pencil-line" disabled={busy} onClick={() => edit(item)}>
              {t('admin.edit')}
            </Button>
            <Button
              kind="ghost"
              icon="check"
              disabled={busy || item.status === 'approved'}
              onClick={() => void approveAgentMemoryEntry(item.id).then(load)}
            >
              {t('admin.approve')}
            </Button>
            <Button
              kind="ghost"
              icon="x"
              disabled={busy || item.status === 'rejected'}
              onClick={() => void rejectAgentMemoryEntry(item.id).then(load)}
            >
              {t('admin.reject')}
            </Button>
            <Button
              kind="ghost"
              icon="archive"
              disabled={busy || item.status === 'archived'}
              onClick={() => void archiveAgentMemoryEntry(item.id).then(load)}
            >
              {t('admin.archive')}
            </Button>
          </Row>
        )}
      />
    </section>
  );
}

function AdminList<T>({ items, render }: { items: readonly T[]; render: (item: T) => ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      {items.map(render)}
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        flexWrap: 'wrap',
        minHeight: 34,
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}
